import { STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT } from "@/constants";
import { ENCOUNTER_FORM_MAPPER } from "@/pages/app/provider/scheduling/appointment/encounter/content-section/VisitNoteForms";
import { formatDate, formatDateTime } from "@/utils/date-time-utility";
import { getAddressFormat } from "@/utils/utils";
import {
  NOTE_PREVIEW_ADDRESS_SEPARATOR,
  NOTE_PREVIEW_CLOCK_DRAWING_SECTION_KEY,
  NOTE_PREVIEW_GROUP_ORDER,
  NOTE_PREVIEW_HEADER_LINE_SEPARATOR,
  NOTE_PREVIEW_LABELS,
  NOTE_PREVIEW_NAME_COMMA_PATTERN,
  NOTE_PREVIEW_PROVIDER_CREDENTIAL_PATTERN,
  NOTE_PREVIEW_STATE_ID_PATTERN,
} from "./note-preview-constant";
import {
  EncounterSummaryReport,
  NotePreviewDetailRow,
  NotePreviewLine,
  NotePreviewSection,
  NotePreviewView,
} from "./note-preview-types";
import { isFreeTextTitle, toPreviewTable } from "./note-preview-line-utils";
import { moveProceduresToEnd } from "./note-preview-section-utils";

const nonEmpty = (parts: (string | null | undefined)[]): string[] =>
  parts.filter((part): part is string => !!part?.trim());

const orBlank = (value?: string | null): string => value?.trim() ?? "";

const joinWithDot = (parts: (string | null | undefined)[]): string =>
  nonEmpty(parts).join(NOTE_PREVIEW_HEADER_LINE_SEPARATOR);

export const templateTitle = (report?: EncounterSummaryReport): string =>
  report?.templateTitle?.trim() ||
  (report?.formType && ENCOUNTER_FORM_MAPPER[report.formType]) ||
  NOTE_PREVIEW_LABELS.defaultTitle;

const patientDisplayName = (report?: EncounterSummaryReport): string =>
  report?.patientName?.trim() ||
  nonEmpty([report?.patientLastName, report?.patientFirstName]).join(", ");

/** "Shelby MD, Thomas" → "Shelby Thomas": credential and comma dropped. */
const stripCredential = (name?: string | null): string | undefined =>
  name
    ?.replace(NOTE_PREVIEW_PROVIDER_CREDENTIAL_PATTERN, "")
    .replace(NOTE_PREVIEW_NAME_COMMA_PATTERN, " ")
    .trim() || undefined;

const renderingProvider = (report?: EncounterSummaryReport) =>
  stripCredential(report?.renderingProvider ?? report?.providerName);

const isFreeTextLine = (line: NotePreviewLine): boolean =>
  !!line.title && isFreeTextTitle(line.title);

/**
 * Drops a free-text line ("Additional note") whose text the backend already merged
 * into the entry it belongs to: only the nearest preceding non-note line is compared,
 * so identical notes under different entries are all kept.
 */
const dedupeNoteLines = (lines: NotePreviewLine[]): NotePreviewLine[] =>
  lines.filter((line, index) => {
    if (!isFreeTextLine(line)) return true;
    const owner = lines
      .slice(0, index)
      .reverse()
      .find((candidate) => !isFreeTextLine(candidate));
    return !owner?.text.toLowerCase().includes(line.text.toLowerCase());
  });

const groupRank = (heading: string): number => {
  const index = (NOTE_PREVIEW_GROUP_ORDER as readonly string[]).indexOf(
    heading
  );
  return index === -1 ? NOTE_PREVIEW_GROUP_ORDER.length : index;
};

/** Subjective, Objective, Assessment, Plan first; unknown headings keep their API order after them. */
const sortGroups = <T extends { heading: string }>(groups: T[]): T[] =>
  [...groups].sort((a, b) => groupRank(a.heading) - groupRank(b.heading));

/** Flattens the grouped report into one preview section per answered report section. */
export const flattenReportSections = (
  report: EncounterSummaryReport | undefined
): NotePreviewSection[] => moveProceduresToEnd(flattenSections(report));

const flattenSections = (
  report: EncounterSummaryReport | undefined
): NotePreviewSection[] =>
  sortGroups(report?.report?.groups ?? [])
    .flatMap((group) =>
      (group.sections ?? []).map((section) => {
        const lines = dedupeNoteLines(
          (section.lines ?? [])
            .filter((line) => !!line.text?.trim() || !!line.title?.trim())
            .map((line) => ({
              title: line.title?.trim() || undefined,
              text: line.text?.trim() ?? "",
              isNested: !!line.nested,
            }))
        );
        const table = toPreviewTable(section.table);
        const image =
          section.key === NOTE_PREVIEW_CLOCK_DRAWING_SECTION_KEY
            ? (report?.clockDrawingUrl ?? undefined)
            : undefined;
        return {
          key: section.key,
          title: section.title,
          group: group.heading,
          lines,
          table,
          image,
          isAnswered: lines.length > 0 || !!table || !!image,
        };
      })
    )
    .filter((section) => section.isAnswered);

const stateName = (state?: string | null): string | undefined =>
  state
    ? (STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT[state] ?? state)
    : undefined;

/** "944 N Clarendon, Soluta, Qui, 4, 80495" → the state id before the zip becomes its name. */
const namePatientAddressState = (address?: string | null): string => {
  if (!address) return "";
  const parts = address
    .split(NOTE_PREVIEW_ADDRESS_SEPARATOR)
    .map((part) => part.trim());
  const stateIndex = parts.length - 2;
  if (stateIndex >= 0 && NOTE_PREVIEW_STATE_ID_PATTERN.test(parts[stateIndex]))
    parts[stateIndex] = stateName(parts[stateIndex]) ?? parts[stateIndex];
  return parts.filter(Boolean).join(`${NOTE_PREVIEW_ADDRESS_SEPARATOR} `);
};

const buildAddressLines = (report?: EncounterSummaryReport): string[] => {
  const address = report?.providerGroupAddress;
  if (!address) return [];
  return nonEmpty([
    getAddressFormat({
      line1: orBlank(address.line1),
      line2: orBlank(address.line2),
      city: orBlank(address.city),
      state: orBlank(address.state),
      country: orBlank(address.country),
      zipcode: orBlank(address.zipcode),
    }),
  ]);
};

const row = (label: string, value?: string | null): NotePreviewDetailRow => ({
  label,
  value: orBlank(value),
});

const buildPatientRows = (
  report: EncounterSummaryReport | undefined
): { leftRows: NotePreviewDetailRow[]; rightRows: NotePreviewDetailRow[] } => ({
  leftRows: [
    row(NOTE_PREVIEW_LABELS.patNumber, report?.ecarehealthPatientId),
    row(NOTE_PREVIEW_LABELS.renderingProvider, renderingProvider(report)),
    row(
      NOTE_PREVIEW_LABELS.dob,
      report?.patientDob ? formatDate(report.patientDob) : undefined
    ),
    row(
      NOTE_PREVIEW_LABELS.location,
      report?.locationName ?? report?.providerGroupName
    ),
    row(
      NOTE_PREVIEW_LABELS.address,
      namePatientAddressState(report?.patientAddress)
    ),
  ],
  rightRows: [
    row(NOTE_PREVIEW_LABELS.name, patientDisplayName(report)),
    row(NOTE_PREVIEW_LABELS.mobile, report?.patientPhone),
    row(
      NOTE_PREVIEW_LABELS.dos,
      report?.appointmentDate
        ? formatDateTime(report.appointmentDate)
        : undefined
    ),
  ],
});

/** Header, patient grid, signature and footer content for the report paper. */
export const buildNotePreviewView = (
  report: EncounterSummaryReport | undefined,
  showEmrLogo: boolean
): NotePreviewView => {
  const isDraft = report?.draft === true;
  const phone = report?.providerGroupPhone ?? undefined;

  return {
    title: joinWithDot([
      templateTitle(report),
      report?.visitType,
      report?.visitMode,
    ]),
    isDraft,
    practice: {
      name:
        report?.providerGroupName || NOTE_PREVIEW_LABELS.defaultPracticeName,
      logo: report?.providerGroupLogo ?? undefined,
      addressLines: buildAddressLines(report),
      phone,
    },
    patient: buildPatientRows(report),
    signature: {
      signerName: orBlank(
        stripCredential(report?.signedByName) ?? renderingProvider(report)
      ),
      role: NOTE_PREVIEW_LABELS.renderingProvider,
      signedAt: report?.signedAt
        ? formatDateTime(report.signedAt)
        : NOTE_PREVIEW_LABELS.notSigned,
      signedAtLabel: NOTE_PREVIEW_LABELS.signedDateTime,
      image: report?.providerSignature ?? undefined,
    },
    addendum: report?.addendumText
      ? {
          text: report.addendumText,
          signerName: orBlank(stripCredential(report.addendumSignedByName)),
          signedAt: orBlank(formatDateTime(report.addendumSignedAt)),
          image: report.addendumSignature ?? undefined,
        }
      : undefined,
    footer: {
      patientLine: joinWithDot([
        patientDisplayName(report),
        report?.ecarehealthPatientId
          ? `${NOTE_PREVIEW_LABELS.patNumberPrefix}${report.ecarehealthPatientId}`
          : undefined,
      ]),
      dosLine: report?.appointmentDate
        ? formatDateTime(report.appointmentDate)
        : "",
      helpLine: phone ? `${NOTE_PREVIEW_LABELS.helpLinePrefix}${phone}` : "",
      version: `${NOTE_PREVIEW_LABELS.versionPrefix}${
        report?.templateVersion?.trim() ||
        NOTE_PREVIEW_LABELS.defaultTemplateVersion
      }`,
      showEmrLogo,
    },
  };
};
